import React from 'react';
import { CheckCircle, Award, Users, Target, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { stats, certifications } from '../mockData';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To provide affordable, high-quality water purification solutions that ensure every home and office in Gurgaon has access to pure, safe drinking water."
    },
    {
      icon: Award,
      title: "Quality First",
      description: "We use only certified, BIS-approved RO systems with advanced multi-stage purification technology to guarantee the highest water quality standards."
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "Your satisfaction is our priority. We offer flexible rental plans, prompt service, and 24/7 support to ensure a hassle-free experience."
    },
    {
      icon: TrendingUp,
      title: "Continuous Innovation",
      description: "We stay updated with the latest water purification technologies to provide cutting-edge solutions for evolving water quality challenges."
    }
  ];

  const timeline = [
    { year: "2019", event: "Founded in Gurgaon", description: "Started with a vision to make pure water accessible to all" },
    { year: "2021", event: "1000+ Customers", description: "Expanded services to cover entire Gurgaon region" },
    { year: "2023", event: "Corporate Solutions", description: "Launched B2B services for offices and institutions" },
    { year: "2025", event: "5000+ Happy Customers", description: "Growing water purification service provider in Gurgaon" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">About RentMyRO</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner for affordable, reliable water purification solutions in Gurgaon since 2019
            </p>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center transform hover:scale-105 transition-transform duration-300">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  RentMyRO Water Tech Pvt. Ltd. was founded in 2019 with a simple yet powerful mission: to make pure, safe drinking water accessible to every household and business in Gurgaon without the burden of high upfront costs.
                </p>
                <p>
                  We recognized that traditional water purifier purchases required significant investment, often deterring families from accessing this essential service. Our innovative rental model changed the game, offering premium RO systems with complete maintenance at affordable monthly rates.
                </p>
                <p>
                  Over the past 5 years, we've grown from a small startup to one of Gurgaon's trusted water purification service providers, serving over 5,000 satisfied customers including homes, offices, schools, and hospitals.
                </p>
                <p className="font-medium text-gray-900">
                  Today, we continue to innovate and expand our services, always keeping our customers' needs at the heart of everything we do.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="/about-team.jpeg" 
                  alt="RentMyRO Team Member"
                  className="w-full h-[500px] object-contain"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="font-semibold text-gray-900">5+ Years of Excellence</span>
                </div>
                <p className="text-sm text-gray-600">Trusted by thousands of customers across Gurgaon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600">A decade of growth and innovation</p>
          </div>
          
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {item.year}
                  </div>
                  {index !== timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-gradient-to-b from-blue-200 to-transparent mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.event}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-8">Certified & Compliant</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-colors duration-300">
                <Award className="w-8 h-8 text-white mx-auto mb-3" />
                <p className="text-white text-sm font-medium">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
